// hooks/useDraggableVideo.js
import { useState, useEffect, useRef, useCallback } from "react";

export const useDraggableVideo = (videoRef, isMobile, simulationType) => {
  const [position, setPosition] = useState({ x: 16, y: 16 });
  const [isDragging, setIsDragging] = useState(false);
  
  // Tracks exactly where the user grabbed the video so it doesn't jump
  const dragOffset = useRef({ x: 0, y: 0 });
  const EDGE_MARGIN = 16;

  const getClient = (event) => {
    if (event.touches && event.touches.length > 0) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    return { x: event.clientX || 0, y: event.clientY || 0 };
  };

  // Helper to get real-time dynamic dimensions instead of hardcoding pixels
  const getDims = useCallback(() => {
    const containerEl = document.querySelector(".video-inner-container") || document.getElementById("callscreen-container");
    
    // Check which element is actually visible to get the correct dimensions
    let el = document.getElementById("video");
    if (!el || el.offsetWidth === 0) {
      el = document.getElementById("avatar-pic");
    }

    if (!containerEl || !el) return null;

    const cRect = containerEl.getBoundingClientRect();
    const vRect = el.getBoundingClientRect();

    return {
      cWidth: cRect.width,
      cHeight: cRect.height,
      cLeft: cRect.left,
      cTop: cRect.top,
      vWidth: vRect.width,
      vHeight: vRect.height,
    };
  }, []);

  const handleDown = (event) => {
    const { x, y } = getClient(event);
    const dims = getDims();
    if (!dims) return;

    setIsDragging(true);

    // Calculate mouse offset relative to the video element's top-left corner
    dragOffset.current = {
      x: x - (dims.cLeft + position.x),
      y: y - (dims.cTop + position.y),
    };
  };

  const snapToCorners = useCallback((currentX, currentY) => {
    const dims = getDims();
    if (!dims) return { x: currentX, y: currentY };

    const { cWidth, cHeight, vWidth, vHeight } = dims;

    const maxX = cWidth - vWidth - EDGE_MARGIN;
    const maxY = cHeight - vHeight - EDGE_MARGIN;

    const corners = [
      { x: EDGE_MARGIN, y: EDGE_MARGIN },
      { x: maxX, y: EDGE_MARGIN },
      { x: EDGE_MARGIN, y: maxY },
      { x: maxX, y: maxY },
    ];

    // Ensure valid coordinates even if container shrinks dramatically
    const validCorners = corners.map((c) => ({
      x: Math.max(0, Math.min(c.x, Math.max(0, maxX))),
      y: Math.max(0, Math.min(c.y, Math.max(0, maxY))),
    }));

    // Find the closest corner
    return validCorners.reduce((prev, curr) =>
      Math.hypot(curr.x - currentX, curr.y - currentY) <
      Math.hypot(prev.x - currentX, prev.y - currentY)
        ? curr
        : prev
    );
  }, [getDims]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (event) => {
      const { x, y } = getClient(event);
      const dims = getDims();
      if (!dims) return;

      // Calculate position relative to the container
      let newX = x - dims.cLeft - dragOffset.current.x;
      let newY = y - dims.cTop - dragOffset.current.y;

      // Clamp coordinates DURING drag so it never gets cut off
      newX = Math.max(0, Math.min(newX, dims.cWidth - dims.vWidth));
      newY = Math.max(0, Math.min(newY, dims.cHeight - dims.vHeight));

      setPosition({ x: newX, y: newY });
    };

    const handleUp = () => {
      setIsDragging(false);
      setPosition((prev) => snapToCorners(prev.x, prev.y));
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging, getDims, snapToCorners]);

  // Window Resize Listener (Fixes the 899px disappearance)
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => snapToCorners(prev.x, prev.y));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [snapToCorners]);

  return { position, isDragging, handleDown };
};